/**
 * Search + ranking for the Find-a-charger list.
 *
 * The search input is the manual charger-code field too, so exact
 * and prefix matches on the GCEV code MUST beat any station-name or
 * address hit. All matching is case-insensitive and whitespace is
 * collapsed so pasted codes with stray spaces still resolve.
 */

const RANK = Object.freeze({
  GCEV_EXACT: 0,
  GCEV_PREFIX: 1,
  NAME_PREFIX: 2,
  NAME_SUBSTR: 3,
  LOCATION_SUBSTR: 4,
  CONNECTOR_SUBSTR: 5,
  NONE: Number.POSITIVE_INFINITY,
});

export function normaliseQuery(raw) {
  return String(raw || '').replace(/\s+/g, '').toUpperCase();
}

function normaliseText(v) {
  return String(v || '').toLowerCase();
}

function rankStation(station, q) {
  if (!q) return RANK.NAME_PREFIX;

  const code = String(station.chargerCode || '').toUpperCase();
  if (code && code === q) return RANK.GCEV_EXACT;
  if (code && code.startsWith(q)) return RANK.GCEV_PREFIX;

  const qLower = q.toLowerCase();
  const name = normaliseText(station.displayName);
  if (name.startsWith(qLower)) return RANK.NAME_PREFIX;
  if (name.includes(qLower)) return RANK.NAME_SUBSTR;

  const location = normaliseText(station.locationText);
  if (location.includes(qLower)) return RANK.LOCATION_SUBSTR;

  const connector = normaliseText(station.connectorType);
  if (connector.includes(qLower)) return RANK.CONNECTOR_SUBSTR;

  return RANK.NONE;
}

/**
 * Filter + rank stations for a given query. Empty query returns
 * every station in its incoming order (the caller applies the
 * distance / alphabetical sort separately).
 */
export function filterAndRank(stations, rawQuery) {
  const q = normaliseQuery(rawQuery);
  if (!q) return { matches: [...(stations || [])], hasQuery: false };

  const withRank = [];
  for (const s of stations || []) {
    const r = rankStation(s, q);
    if (r !== RANK.NONE) withRank.push({ station: s, rank: r });
  }
  withRank.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    // Stable-ish tiebreaker on display name so results don't jitter.
    return String(a.station.displayName || '').localeCompare(
      String(b.station.displayName || ''),
    );
  });
  return { matches: withRank.map((w) => w.station), hasQuery: true };
}

export { RANK as SEARCH_RANK };
